import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    FaUserTie,
    FaTractor,
    FaSeedling,
    FaHorse,
    FaGraduationCap,
    FaBriefcase,
    FaComments,
    FaForumbee
} from "react-icons/fa";

import {
    countByNatureActivityType,
    countSpecialitesByNatureActivityType,
    countActivityProfsByNatureActivityType
} from "../../pages/userapp/ServiceUserApp";

import {
    countAllConsultations
} from "../../pages/consultationrequest/consultationRequestService";

import {
    countTopics
} from "../../pages/forum/forumTopicService";


import "../../styles/StatsCards.css";


export default function StatsCards() {

    const { t } = useTranslation();

    const [stats, setStats] = useState({

        consultantAgriculture: 0,
        consultantElevage: 0,

        felahAgriculture: 0,
        felahElevage: 0,

        specialiteAgriculture: 0,
        specialiteElevage: 0,

        activityProfAgriculture: 0,
        activityProfElevage: 0,

        consultations: 0,

        topics: 0
    });

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);


    // ============================================================
    // CHARGEMENT DES STATISTIQUES
    // ============================================================

    useEffect(() => {

        const loadStatistics = async () => {

            try {

                setLoading(true);
                setError(null);

                const [
                    consultantAgricultureResponse,
                    consultantElevageResponse,

                    felahAgricultureResponse,
                    felahElevageResponse,

                    specialiteAgricultureResponse,
                    specialiteElevageResponse,

                    activityProfAgricultureResponse,
                    activityProfElevageResponse,

                    consultationsResponse,

                    topicsResponse

                ] = await Promise.all([

                    countByNatureActivityType("P", "C"),
                    countByNatureActivityType("P", "L"),

                    countByNatureActivityType("F", "C"),
                    countByNatureActivityType("F", "L"),

                    countSpecialitesByNatureActivityType("C"),
                    countSpecialitesByNatureActivityType("L"),

                    countActivityProfsByNatureActivityType("C"),
                    countActivityProfsByNatureActivityType("L"),

                    countAllConsultations(),

                    countTopics()
                ]);


                setStats({

                    consultantAgriculture:
                        Number(consultantAgricultureResponse.data) || 0,

                    consultantElevage:
                        Number(consultantElevageResponse.data) || 0,

                    felahAgriculture:
                        Number(felahAgricultureResponse.data) || 0,

                    felahElevage:
                        Number(felahElevageResponse.data) || 0,

                    specialiteAgriculture:
                        Number(specialiteAgricultureResponse.data) || 0,

                    specialiteElevage:
                        Number(specialiteElevageResponse.data) || 0,

                    activityProfAgriculture:
                        Number(activityProfAgricultureResponse.data) || 0,

                    activityProfElevage:
                        Number(activityProfElevageResponse.data) || 0,

                    consultations:
                        Number(consultationsResponse.data) || 0,

                    topics:
                        Number(topicsResponse.data) || 0
                });

            } catch (err) {

                console.error(
                    "Erreur lors du chargement des statistiques :",
                    err
                );

                setError(
                    t("statistics.loadingError")
                );

            } finally {

                setLoading(false);
            }
        };

        loadStatistics();

    }, [t]);


    // ============================================================
    // STATISTIQUES
    // ============================================================

    const cards = [

        {
            id: 1,

            title: t("statistics.consultants"),

            value:
                stats.consultantAgriculture +
                stats.consultantElevage,

            icon: <FaUserTie />,

            color: "success",

            description:
                t("statistics.consultantsDescription")
        },


        {
            id: 2,

            title: t("statistics.felah"),

            value:
                stats.felahAgriculture +
                stats.felahElevage,

            icon: <FaTractor />,

            color: "primary",

            description:
                t("statistics.felahDescription")
        },


        {
            id: 3,

            title: t("statistics.agriculture"),

            value:
                stats.felahAgriculture,

            icon: <FaSeedling />,

            color: "warning",

            description:
                t("statistics.agricultureDescription")
        },


        {
            id: 4,

            title: t("statistics.elevage"),

            value:
                stats.felahElevage,

            icon: <FaHorse />,

            color: "danger",

            description:
                t("statistics.elevageDescription")
        },


        {
            id: 5,

            title: t("statistics.specialites"),

            value:
                stats.specialiteAgriculture +
                stats.specialiteElevage,

            icon: <FaGraduationCap />,

            color: "info",

            description:
                t("statistics.specialitesDescription")
        },


        {
            id: 6,

            title: t("statistics.activities"),

            value:
                stats.activityProfAgriculture +
                stats.activityProfElevage,

            icon: <FaBriefcase />,

            color: "secondary",

            description:
                t("statistics.activitiesDescription")
        },


        {
            id: 7,

            title: t("statistics.consultations"),

            value:
                stats.consultations,

            icon: <FaComments />,

            color: "info",

            description:
                t("statistics.consultationsDescription")
        },


        {
            id: 8,

            title: t("statistics.forum"),

            value:
                stats.topics,

            icon: <FaForumbee />,

            color: "secondary",

            description:
                t("statistics.forumDescription")
        }
    ];


    // ============================================================
    // LOADING
    // ============================================================

    if (loading) {

        return (

            <section className="stats-section">

                <div className="container">

                    <div className="row g-3">

                        {[1, 2, 3, 4, 5, 6, 7, 8].map(
                            (item) => (

                                <div
                                    key={item}
                                    className="col-xl-3 col-lg-4 col-md-6"
                                >

                                    <div className="stats-card">

                                        <div className="stats-body text-center">

                                            <div
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                            />

                                            <div className="stats-description mt-2">

                                                {t(
                                                    "statistics.loading"
                                                )}

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            )
                        )}

                    </div>

                </div>

            </section>
        );
    }


    // ============================================================
    // ERREUR
    // ============================================================

    if (error) {

        return (

            <section className="stats-section">

                <div className="container">

                    <div className="alert alert-warning text-center">

                        {error}

                    </div>

                </div>

            </section>
        );
    }


    // ============================================================
    // AFFICHAGE
    // ============================================================

    return (

        <section className="stats-section">

            <div className="container">

                <div className="row g-3 justify-content-center">

                    {cards.map((item) => (

                        <div
                            key={item.id}
                            className="col-xl-3 col-lg-4 col-md-6"
                        >

                            <div className="stats-card">

                                <div
                                    className={`stats-icon bg-${item.color}`}
                                >
                                    {item.icon}
                                </div>


                                <div className="stats-body">

                                    <div className="stats-value">

                                        {Number(item.value).toLocaleString(
                                            "fr-FR"
                                        )}

                                    </div>


                                    <div className="stats-title">

                                        {item.title}

                                    </div>


                                    <div className="stats-description">

                                        {item.description}

                                    </div>

                                </div>

                            </div>

                        </div>

                    ))}

                </div>

            </div>

        </section>
    );
}
